import React from 'react';
import dayjs from 'dayjs';
import { CustomerModel, ReservationGuestReferenceModel, ReservationModel } from '@common/modelDefinition';
import translate from '@data/translations';
import { DocumentScanner } from './DocumentScanner';
import { paymentCategoryEVisitor } from '@common/eGuestsData/eVisitor';
import { closeModal } from '@components/modal';
import useStore from '@data/state/zustand';
import ConfirmButtons from '@components/buttons/confirmButtons';
import { CustomDayPicker } from '@components/DayPicker';

interface VisaDataModalProps {
    visaData: ReservationGuestReferenceModel | undefined;
    saveHandler: any;
    customer: CustomerModel;
    reservation: ReservationModel;
}

const VisaDataModal = (props: VisaDataModalProps) => {
    const { visaData, saveHandler, customer, reservation } = props;
    const [visaDataClone, setVisaDataClone] = React.useState({ ...visaData } as ReservationGuestReferenceModel);
    const [scannedCustomer, setScannedCustomer] = React.useState(null as CustomerModel | null);
    const [showDocumentScannerData, setShowDocumentScannerData] = React.useState(false);
    const [customersAfterScan, setCustomersAfterScan] = React.useState({
        data: [],
        typeOfScan: 0,
        visaInfo: {} as ReservationGuestReferenceModel,
        customer: null as CustomerModel | null,
    });

    const docScan = useStore((appState: any) => appState.model.DocumentScan);
    const otherSettings = useStore((appState: any) => appState.otherSettings);

    const documentId = React.useRef(null);
    const documentType = React.useRef(null);

    React.useEffect(() => {
        if (visaData) {
            checkValidationInputs(visaData);
            setVisaDataClone({ ...visaData });
        }
        // eslint-disable-next-line
    }, [visaData]);

    React.useEffect(() => {
        const loadData = async () => {
            if (customersAfterScan.visaInfo.customerId) {
                let _visaData: any = { ...visaDataClone };
                const scannedVisaData: any = { ...customersAfterScan.visaInfo };
                Object.keys(visaDataClone).forEach((d: any) => {
                    if (scannedVisaData && scannedVisaData[d]) {
                        _visaData[d] = scannedVisaData[d];
                    }
                });
                checkValidationInputs(_visaData);
                setVisaDataClone(_visaData);
            }
            if (customersAfterScan.customer) {
                setScannedCustomer({ ...customersAfterScan.customer });
            }
        };

        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, [customersAfterScan]);

    const handleInputChange = (e: any) => {
        const _visaDataClone: any = { ...visaDataClone };
        _visaDataClone[e.target.name] = e.target.value === 'noSelected' ? null : e.target.value;
        if (_visaDataClone[e.target.name] === '') {
            _visaDataClone[e.target.name] = null;
        }
        if (e.target.name === 'documentId' || e.target.name === 'documentType') {
            checkValidationInputs(_visaDataClone);
        }
        setVisaDataClone({ ..._visaDataClone });
    };

    const handleDate = (attribute: string, d: any) => {
        if (attribute === 'visaEntryDate') {
            let date = dayjs(d).format('YYYY-MM-DD');
            visaDataClone['visaEntryDate'] = date;
        }
        if (attribute === 'visaValidityPeriod') {
            let date = dayjs(d).format('YYYY-MM');
            visaDataClone['visaValidityPeriod'] = date;
        }
        if (attribute === 'documentValidityPeriod') {
            let date = dayjs(d).format('YYYY-MM');
            visaDataClone['documentValidityPeriod'] = date;
        }
        if (attribute === 'residencePermitExpiryDate') {
            let date = dayjs(d).format('YYYY-MM');
            visaDataClone['residencePermitExpiryDate'] = date;
        }
        setVisaDataClone({ ...visaDataClone });
    };

    const checkValidationInputs = (objToValidate: any) => {
        let validationResults = getValidationInputs(objToValidate);
        markInvalidInputRefs(validationResults);
    };

    const markInvalidInputRefs = (validationResults: any) => {
        //generic
        const { invalidInputs, validInputs } = validationResults;
        invalidInputs.forEach((input: any) => {
            if (input && input.current) {
                let classes = input.current.className.split(' ');
                if (!classes.includes('is-invalid')) {
                    classes.push('is-invalid');
                }
                input.current.className = classes.join(' ');
            }
        });
        validInputs.forEach((input: any) => {
            if (input && input.current) {
                let classes = input.current.className.split(' ');
                if (classes.includes('is-invalid')) {
                   classes = classes?.filter((i: any) => i !== 'is-invalid')
                }
                input.current.className = classes.join(' ');
            }
        });
    };

    const getValidationInputs = (data: any) => {
        const invalidInputs = [],
            validInputs = [];
        // clearValidationRefs();
        if (data.documentId === null || (data.documentId && data.documentId.trim() === '')) {
            invalidInputs.push(documentId);
        } else {
            validInputs.push(documentId);
        }
        if (data.documentType === null || (data.documentType && data.documentType.trim() === '')) {
            invalidInputs.push(documentType);
        } else {
            validInputs.push(documentType);
        }
        return { invalidInputs, validInputs };
    };

    return (
        <div className="">
            <div className="display-flex">
                <div
                    className="button-info pointer"
                    onClick={(e) => {
                        e.preventDefault();
                        setShowDocumentScannerData(!showDocumentScannerData);
                    }}
                >
                    <small>{translate('READ FROM SCANNER')}</small>
                </div>
            </div>

            {showDocumentScannerData ? (
                <DocumentScanner
                    docScan={docScan}
                    customer={customer}
                    reservation={reservation}
                    setCustomersAfterScan={setCustomersAfterScan}
                    setShowDocumentScannerData={setShowDocumentScannerData}
                    visa={true}
                />
            ) : (
                <>
                    {/* Red 1: visaTypeAndNumber + visaTypeCode */}
                    <div className="mt-12p display-flex">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Visa number')}
                            </label>
                            <input
                                autoComplete="off"
                                name="visaTypeAndNumber"
                                placeholder={translate('246694821')}
                                type="text"
                                className="input input-modal"
                                value={visaDataClone.visaTypeAndNumber ? visaDataClone.visaTypeAndNumber : ''}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Visa type')}
                            </label>
                            <select
                                name="visaTypeCode"
                                className="input input-modal"
                                value={visaDataClone.visaTypeCode ? visaDataClone.visaTypeCode : 'K'}
                                onChange={(e) => {
                                    handleInputChange(e);
                                }}
                            >
                                <option value={'K'}>{translate('Kratkoročno')}</option>
                                <option value={'D'}>{translate('Dugoročno')}</option>
                            </select>
                        </div>
                    </div>
                    {/* Red 2: visaIssuancePlace + visaValidityPeriod */}
                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Visa Validity Period')}
                            </label>
                            <div>
                                <CustomDayPicker
                                    value={
                                        visaDataClone.visaValidityPeriod
                                            ? new Date(visaDataClone.visaValidityPeriod)?.getTime()
                                            : null
                                    }
                                    onSelect={async (d: any) => {
                                        handleDate('visaValidityPeriod', d);
                                    }}
                                    fromYear={new Date().getFullYear() - 5}
                                    toYear={new Date()?.getFullYear() + 100}
                                    width="auto"
                                />
                            </div>
                        </div>
                         <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Visa issuance place')}
                            </label>
                            <input
                                autoComplete="off"
                                name="visaIssuancePlace"
                                placeholder={translate('Visa issuance place')}
                                type="text"
                                className="input input-modal"
                                value={visaDataClone.visaIssuancePlace ? visaDataClone.visaIssuancePlace : ''}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    {/* Red 3: visaEntryDate + placeEntry */}
                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Date entry in country')}
                            </label>
                            <div>
                                <CustomDayPicker
                                    value={
                                        visaDataClone.visaEntryDate
                                            ? new Date(visaDataClone.visaEntryDate)?.getTime()
                                            : null
                                    }
                                    onSelect={async (d: any) => {
                                        handleDate('visaEntryDate', d);
                                    }}
                                    fromYear={new Date().getFullYear() - 5}
                                    toYear={new Date()?.getFullYear() + 100}
                                    width="auto"
                                />
                            </div>
                        </div>
                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Entry place')}
                            </label>
                            <input
                                autoComplete="off"
                                name="placeEntry"
                                placeholder={translate('Kamensko')}
                                type="text"
                                className="input input-modal"
                                value={visaDataClone.placeEntry ? visaDataClone.placeEntry : ''}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    {/* Red 4: residencePermitExpiryDate */}
                    <div className="display-flex mt-12p">
                        <div className="flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Right to reside (in the country)')}
                            </label>
                            <div>
                                <CustomDayPicker
                                    value={
                                        visaDataClone.residencePermitExpiryDate
                                            ? new Date(visaDataClone.residencePermitExpiryDate)?.getTime()
                                            : null
                                    }
                                    onSelect={async (d: any) => {
                                        handleDate('residencePermitExpiryDate', d);
                                    }}
                                    fromYear={new Date().getFullYear() - 5}
                                    toYear={new Date()?.getFullYear() + 100}
                                    width="auto"
                                />
                            </div>
                        </div>
                    </div>
                    <hr/>
                    {/* Red 5: documentId + documentType */}
                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Document id')}
                            </label>
                            <input
                                ref={documentId}
                                autoComplete="off"
                                name="documentId"
                                placeholder={
                                    customer.documentId ? "'" + customer.documentId + "'" : translate('128674512')
                                }
                                type="text"
                                className="input input-modal"
                                value={visaDataClone.documentId ? visaDataClone.documentId : ''}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Document type')}
                            </label>
                            <select
                                ref={documentType}
                                name="documentType"
                                className="input input-modal"
                                value={visaDataClone.documentType ? visaDataClone.documentType : ''}
                                onChange={(e) => {
                                    handleInputChange(e);
                                }}
                            >
                                <option value={0}>{translate('Nothing selected')}</option>
                                <option value={'P'}>{translate('Passport')}</option>
                                <option value={'I'}>{translate('Identity card')}</option>
                                <option value={'V'}>{translate('Visa')}</option>
                                <option value={'D'}>{translate('Driving licence')}</option>
                                <option value={'X'}>{translate('Other')}</option>
                            </select>
                        </div>
                    </div>
                    {/* Red 6: documentValidityPeriod + identityCardIssuer */}
                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Document Validity Period')}
                            </label>
                            <div>
                                <CustomDayPicker
                                    value={
                                        visaDataClone.documentValidityPeriod
                                            ? new Date(visaDataClone.documentValidityPeriod)?.getTime()
                                            : null
                                    }
                                    onSelect={async (d: any) => {
                                        handleDate('documentValidityPeriod', d);
                                    }}
                                    fromYear={new Date().getFullYear() - 5}
                                    toYear={new Date()?.getFullYear() + 100}
                                    width="auto"
                                />
                            </div>
                        </div>
                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Issuing authority of travel document')}
                            </label>
                            <input
                                autoComplete="off"
                                name="identityCardIssuer"
                                placeholder={translate('e.g. MUP BiH')}
                                type="text"
                                className="input input-modal"
                                value={visaDataClone.identityCardIssuer ? visaDataClone.identityCardIssuer : ''}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    {otherSettings?.csvDownloadTemplate === 'eVisitor' ? (
                        <div className="display-flex mt-12p">
                            <div className="flex">
                                <label className="text-bold ml-4p" htmlFor="">
                                    {translate('paymentCategory')}:
                                </label>
                                <select
                                    name="paymentCategory"
                                    className="input input-modal"
                                    value={visaDataClone.paymentCategory ? visaDataClone.paymentCategory : ''}
                                    onChange={(e: any) => {
                                        handleInputChange(e);
                                    }}
                                >
                                    <option value={'noSelected'}>{translate('Nothing selected')}</option>
                                    {paymentCategoryEVisitor?.map((p, key) => {
                                        return (
                                            <option key={key} value={p.Code}>
                                                {p.Name}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    ) : null}
                    <div className="mt-12p">
                        <label className="text-bold ml-4p" htmlFor="">
                            {translate('Notes')}
                        </label>
                        <input
                            autoComplete="off"
                            name="note"
                            placeholder={translate('Notes')}
                            type="text"
                            className="input input-modal"
                            value={visaDataClone.note ? visaDataClone.note : ''}
                            onChange={handleInputChange}
                        />
                    </div>
                    <br />
                    <div className="flex-center-center mt-20p">
                        <ConfirmButtons
                            onConfirm={async () => {
                                let updateCustomer = false;
                                if (
                                    scannedCustomer ||
                                    (visaDataClone.documentId && visaDataClone.documentId !== customer.documentId) ||
                                    (visaDataClone.documentType && visaDataClone.documentType !== customer.documentType)
                                    
                                ) {
                                    updateCustomer = true;
                                }
                                if (!visaDataClone.visaTypeCode) {
                                    visaDataClone.visaTypeCode = 'K';
                                }
                                await saveHandler(visaDataClone, scannedCustomer, updateCustomer);
                            }}
                            onCancel={closeModal}
                            confirmText={'Save'}
                        />
                    </div>{' '}
                </>
            )}
        </div>
    );
};

export default VisaDataModal;
